'use client'

import { algoliaFetch } from '@repo/algolia/client'
import { algoliaPrimaryIndexName } from '@repo/algolia/config'
import { ALGOLIA_EVENTS } from '@repo/algolia/insights'
import { AlgoliaApiSearchResponse, AlgoliaHit, AlgoliaHitCollectionData, AlgoliaSearchRequest } from '@repo/algolia/types'
import { mapAlgoliaHitsToElevarViewSearchResultsData } from '@repo/elevar/mapper'
import { NavigationQueryResult } from '@repo/sanity/types'
import { Button } from '@repo/ui/components/button'
import { Input } from '@repo/ui/components/input'
import ProductPreviewCard from '@repo/ui/components/product-preview-card'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@repo/ui/components/sheet'
import { Spinner } from '@repo/ui/components/spinner'
import useIdleObserver from '@repo/ui/hooks/useIdleObserver'
import { createHrefForDocument } from '@repo/url/href'
import { sleep } from '@repo/utils/sleep'
import { LinkProps } from 'next/dist/client/link'
import Link from 'next/link'
import { AnchorHTMLAttributes, FunctionComponent, useCallback, useEffect, useState } from 'react'
import { SHEET, useSheet } from '../../../providers/SheetProvider'
import { useTelemetry } from '../../../providers/TelemetryProvider'
import { PageDto } from '../../../types/page.types'
import AddToWishlistIcon from '../../sections/wishlist/AddToWishlistIcon'
import AlgoliaHitImage from './AlgoliaHitImage'
import SearchTriggerButton from './SearchTriggerButton'

interface Props {
  pageDto: PageDto
  navigation: NavigationQueryResult
}

const SearchSheet: FunctionComponent<Props> = ({ pageDto, navigation }) => {
  const { userId } = useTelemetry()
  const { isSheetOpen, closeSheet } = useSheet()
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [hits, setHits] = useState<AlgoliaHit[]>([])
  const [collections, setCollections] = useState<AlgoliaHitCollectionData[]>([])
  const [queryID, setQueryID] = useState<string | undefined>(undefined)
  const { trackElevarViewSearchResultsEvent, trackAlgoliaViewObjectIDsEvent } = useTelemetry()
  const indexName = algoliaPrimaryIndexName(pageDto.params.locale)

  const fetchResults = useCallback(
    async (query: string) => {
      try {
        setLoading(true)

        const body: AlgoliaSearchRequest = {
          query,
          length: 200,
          hitsPerPage: 200,
          analytics: true,
          clickAnalytics: true,
          enablePersonalization: false,
          userToken: userId || undefined,
          facetFilters: ['objectType:product'],
          removeWordsIfNoResults: 'lastWords',
        }

        // Algolia search request.
        // @see https://www.algolia.com/doc/rest-api/search/#tag/Search/operation/searchSingleIndex
        const response = await algoliaFetch<AlgoliaSearchRequest, AlgoliaApiSearchResponse>({
          body,
          path: `/1/indexes/${indexName}/query`,
        })

        setHits(response?.hits || [])
        setQueryID(response?.queryID)

        // Dedupe return collections from all hits.
        const allCollections = (response?.hits ?? []).flatMap((hit) => [hit.topLevelCollection, hit.secondLevelCollection].filter(Boolean)) as AlgoliaHitCollectionData[]
        const collectionsMap = new Map(allCollections.map((collection) => [collection.urlSlug, collection]))
        setCollections(Array.from(collectionsMap.values()))
      } catch (error: unknown) {
        console.error(`Search error.`, { error, query })
      } finally {
        setLoading(false)
      }
    },
    [indexName, userId],
  )

  // Observe search input.
  useEffect(() => {
    // Clear
    if (query.length === 0) {
      setHits([])
      setCollections([])
    }

    // Do nothing, user might be typing.
    if (query.length < 2) {
      return
    }

    // Debounce
    const handler = setTimeout(() => {
      fetchResults(query)
    }, 200)

    return () => clearTimeout(handler)
  }, [fetchResults, query])

  // Telemetry
  useIdleObserver(
    () => {
      if (hits.length > 0) {
        trackElevarViewSearchResultsEvent(mapAlgoliaHitsToElevarViewSearchResultsData(hits, pageDto.params.locale))

        trackAlgoliaViewObjectIDsEvent({
          eventName: 'View Search Results',
          objectIDs: hits.map((hit) => hit.objectID),
        })
      }
    },
    {},
    [hits, pageDto.params.locale],
  )

  // Improves behaviour to have slight close delay.
  const handleHref = () => {
    sleep(500).then(() => closeSheet())
  }

  return (
    <>
      <SearchTriggerButton pageDto={pageDto} navigation={navigation} />
      <Sheet open={isSheetOpen(SHEET.SEARCH)} onOpenChange={(open) => !open && closeSheet()}>
        <SheetContent side='all'>
          <SheetHeader>
            <SheetTitle>{pageDto.dictionary?.navigation?.search || 'Search'}</SheetTitle>
          </SheetHeader>
          <SheetDescription className='sr-only'>{pageDto.dictionary?.navigation?.searchourcatalog || 'Search our catalog.'}</SheetDescription>
          <div className='flex flex-col gap-4 overflow-y-auto p-3'>
            <div className='relative'>
              <Input
                type='search'
                name='search'
                variant='search'
                value={query}
                className='w-11/12'
                placeholder={pageDto.dictionary?.search?.typehere || 'Type here...'}
                onChange={(e) => setQuery(e.target.value)}
              />
              <Spinner loading={loading} className='absolute right-2 top-1/2 -translate-y-1/2 lg:right-4' />
            </div>
            {hits.length > 0 && (
              <div className='flex flex-col gap-8 lg:gap-10' data-query-id={queryID} aria-live='polite' aria-busy={loading}>
                {collections.length > 0 && (
                  <section className='gap-miista-page-padding flex flex-col'>
                    <h3 className='pl-miista-product-preview-inner flex items-center uppercase'>{pageDto.dictionary?.search?.categories || 'Categories'}</h3>
                    <ul role='list' className='flex w-full flex-wrap gap-px py-1'>
                      {collections.map((collection) => {
                        const href = createHrefForDocument('collection', collection.urlSlug, pageDto.params.locale)

                        return (
                          <li key={collection.urlSlug}>
                            <Button variant='secondaryStatic' asChild={true}>
                              <Link href={href} prefetch={true} onClick={handleHref}>
                                {collection.title}
                              </Link>
                            </Button>
                          </li>
                        )
                      })}
                    </ul>
                  </section>
                )}
                <section className='gap-miista-page-padding flex flex-col'>
                  <h3 className='pl-miista-product-preview-inner flex items-center uppercase'>{pageDto.dictionary?.search?.searchresults || 'Search Results'}</h3>
                  <ol className='4xl:grid-cols-6 grid grid-cols-2 gap-4 pb-4 lg:grid-cols-3 xl:grid-cols-4'>
                    {hits.map((hit, i) => {
                      const sizes = '(min-width: 2240px) 17vw, (min-width: 1280px) 25vw, (min-width: 1024px) 33vw, 50vw'
                      const href = createHrefForDocument('product', hit.urlSlug, pageDto.params.locale) || ''

                      const linkProps: LinkProps & AnchorHTMLAttributes<HTMLAnchorElement> = {
                        href,
                        onClick: handleHref,
                        title: hit.pageTitle || hit.title || undefined,
                      }

                      const eventData = JSON.stringify({
                        eventName: 'Click Search Result',
                        objectIDs: [hit.objectID],
                        positions: [i + 1],
                        queryID: queryID || '',
                      })

                      return (
                        <ProductPreviewCard
                          titleAs='h4'
                          key={hit.objectID}
                          title={hit.title || undefined}
                          price={hit.shopifyPrice}
                          discount={hit.shopifyDiscount}
                          currencyCode={hit.shopifyCurrency}
                          mediaPrimary={<AlgoliaHitImage image={hit.thumbnails?.[0]} priority={i <= 8} sizes={sizes} fallbackAlt={hit.title || undefined} />}
                          mediaSecondary={<AlgoliaHitImage image={hit.thumbnails?.[1]} priority={false} sizes={sizes} fallbackAlt={hit.title || undefined} />}
                          linkProps={linkProps}
                          wishlist={<AddToWishlistIcon pageDto={pageDto} urlSlug={hit.urlSlug} />}
                          data-telemetry-event={ALGOLIA_EVENTS.CLICK_OBJECT_IDS_AFTER_SEARCH}
                          data-telemetry-event-data={eventData}
                        />
                      )
                    })}
                  </ol>
                </section>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

export default SearchSheet
